import {
  useMutation,
  UseMutationResult,
  useQuery,
  useInfiniteQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  getApiMultimediaGetOrgMultimediaByTypeOrgIdMultimediaType,
  getApiSettingsGetOrCreateUserSettings,
  getApiSettingsGetSurgicalChecklistFilterSettings,
  getApiSurgicalChecklistGetORDisplay,
  getApiSurgicalChecklistGetSurgicalChecklistDetails,
  getApiUserGetLoggedInUserInfo,
  postApiCHCheckEligibility,
  postApiSettingsCreateOrUpdateUserSettings,
  postApiSurgicalChecklistGetSurgicalChecklists,
  putApiSurgicalChecklistSignSurgicalChecklistPhase,
  putApiSurgicalChecklistUnsignSurgicalChecklistPhase,
} from '@/endpoints';
import {
  GetApiSurgicalChecklistGetORDisplayParams,
  GetApiSurgicalChecklistGetSurgicalChecklistDetailsParams,
  CheckEligibilityResponseDTO,
  EligibilityRequestDTO,
  SurgicalChecklistsRequestDTO,
  LoggedInUserInfoDTO,
  None,
  PutApiSurgicalChecklistUnsignSurgicalChecklistPhaseParams,
  SignSurgicalChecklistPhaseRequestDTO,
  SurgicalChecklistDetailResponseDTO,
  SurgicalChecklistDTO,
  SurgicalChecklistDTOPaginatedResponseDTO,
  SurgicalChecklistORDisplayResponseDTO,
  SettingsDTO,
  MultiMediaResponseDTO,
  UserSettingDTO,
} from '@/dtos';
import isNil from 'lodash/isNil';
import {
  isNilOrZero,
} from '@/utils/misc';
import {
  datadogLogs,
} from '@datadog/browser-logs';
import {
  enabledLogs,
} from '@/config/datadogBrowserLogs';
import {
  getDisplayName,
} from '@/utils/names';
import usePrefetchedData from './usePrefetchedData';

export function useGetLoggedInUserInfo(): UseMutationResult<LoggedInUserInfoDTO, Error, None> {
  return useMutation<LoggedInUserInfoDTO, Error, None>({
    mutationFn: getApiUserGetLoggedInUserInfo,
    onSuccess: (data) => {
      try {
        if(enabledLogs){
          const { userId, email, formattedNames } = data?.user ?? {};
          const user = {
            id: userId?.toString() ?? '',
            email: email ?? '',
            name: getDisplayName(formattedNames),
          };
          datadogLogs.setUser(user);
        }
      } catch {
        // ignore
      }
    },
  });
}

export function useGetOrgMultimediaByType(orgId: number, multimediaType: string, enabled: boolean = true) {
  return useQuery<MultiMediaResponseDTO, Error, MultiMediaResponseDTO>({
    queryKey: ['org-multimedia', orgId, multimediaType],
    queryFn: () => getApiMultimediaGetOrgMultimediaByTypeOrgIdMultimediaType(orgId, multimediaType),
    enabled: enabled && orgId > 0 && !isNil(multimediaType),
  });
}

export function useGetSurgicalChecklists(
  payload: SurgicalChecklistsRequestDTO | undefined,
  enabled: boolean = true,
) {
  return useQuery<SurgicalChecklistDTO[], Error, SurgicalChecklistDTO[]>({
    queryKey: [
      'surgical-checklists',
      payload?.dateRange?.start ?? '',
      payload?.dateRange?.end ?? '',
      payload?.patientSearchText ?? '',
      payload?.filterQuery?.surgicalChecklistStatusList?.join(',') ?? '',
      payload?.filterQuery?.providerIdList?.join(',') ?? '',
    ],
    queryFn: async () => {
      const res: SurgicalChecklistDTOPaginatedResponseDTO = await postApiSurgicalChecklistGetSurgicalChecklists(payload as SurgicalChecklistsRequestDTO);
      return (res?.data as SurgicalChecklistDTO[]) ?? [];
    },
    enabled: enabled && !isNil(payload),
    gcTime: 0,
  });
}

export function useGetSurgicalChecklistsInfinite(
  payload: SurgicalChecklistsRequestDTO | undefined,
  enabled: boolean = true,
) {
  return useInfiniteQuery<SurgicalChecklistDTOPaginatedResponseDTO, Error>({
    queryKey: [
      'surgical-checklists-infinite',
      payload?.dateRange?.start ?? '',
      payload?.dateRange?.end ?? '',
      payload?.patientSearchText ?? '',
      payload?.filterQuery?.surgicalChecklistStatusList?.join(',') ?? '',
      payload?.filterQuery?.providerIdList?.join(',') ?? '',
      payload?.pageRequest?.pageSize ?? 0,
    ],
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const res: SurgicalChecklistDTOPaginatedResponseDTO = await postApiSurgicalChecklistGetSurgicalChecklists({
        ...(payload as SurgicalChecklistsRequestDTO),
        pageRequest: { page: pageParam as number, pageSize: payload?.pageRequest?.pageSize ?? 25 },
      });
      return res;
    },
    getNextPageParam: (lastPage, allPages) => (
      lastPage?.pageProperties?.hasNextPage ? allPages.length : undefined
    ),
    enabled: enabled && !isNil(payload),
    gcTime: 0,
  });
}

export function useGetSurgicalChecklistDetails(
  data: GetApiSurgicalChecklistGetSurgicalChecklistDetailsParams,
  enabled: boolean = true,
) {
  return useQuery<SurgicalChecklistDetailResponseDTO, Error, SurgicalChecklistDetailResponseDTO>({
    queryKey: ['surgical-checklist-details', data.surgicalChecklistId],
    queryFn: () => getApiSurgicalChecklistGetSurgicalChecklistDetails(data),
    enabled: enabled && !isNilOrZero(data?.surgicalChecklistId),
  });
}

export function useSignSurgicalChecklistPhase() {
  return useMutation<SignSurgicalChecklistPhaseRequestDTO, Error, None>({
    mutationFn: (data) => putApiSurgicalChecklistSignSurgicalChecklistPhase(data),
  });
}

export function useUnsignSurgicalChecklistPhase() {
  return useMutation<None, Error, PutApiSurgicalChecklistUnsignSurgicalChecklistPhaseParams>({
    mutationFn: (data) => putApiSurgicalChecklistUnsignSurgicalChecklistPhase(data),
  });
}

export function useGetORDisplay(data: GetApiSurgicalChecklistGetORDisplayParams, enabled: boolean = true) {
  return useQuery<SurgicalChecklistORDisplayResponseDTO, Error, SurgicalChecklistORDisplayResponseDTO>({
    queryKey: ['or-display', data?.surgicalChecklistId ?? ''],
    queryFn: () => getApiSurgicalChecklistGetORDisplay(data),
    enabled: enabled && !isNilOrZero(data?.surgicalChecklistId),
    gcTime: 0,
  });
}

export function useCheckEligibility(): UseMutationResult<CheckEligibilityResponseDTO | void, Error, EligibilityRequestDTO> {
  return useMutation<CheckEligibilityResponseDTO | void, Error, EligibilityRequestDTO>({
    mutationFn: (data) => postApiCHCheckEligibility(data),
  });
}

export function useGetSurgicalChecklistFilterSettings(enabled: boolean = true) {
  return usePrefetchedData<SettingsDTO>({
    key: ['surgical-checklist-filter-settings'],
    fetchFn: getApiSettingsGetSurgicalChecklistFilterSettings,
    enabled: enabled,
  });
}

export function useGetOrCreateUserSettings(enabled: boolean = true): UseQueryResult<UserSettingDTO, Error> {
  return useQuery<UserSettingDTO, Error>({
    queryKey: ['user-settings'],
    queryFn: getApiSettingsGetOrCreateUserSettings,
    enabled: enabled,
  });
}

export function useCreateOrUpdateUserSettings(): UseMutationResult<UserSettingDTO, Error, UserSettingDTO> {
  return useMutation<UserSettingDTO, Error, UserSettingDTO>({
    mutationFn: (data) => postApiSettingsCreateOrUpdateUserSettings(data),
  });
}
